import { useGetTestingSetupPlanQuery } from '../services/sessionMonitoringApi'
import { useState } from 'react'
import { useGetTestingSchedulesQuery, useSaveTestingScheduleMutation } from '../services/projectDataApi'
import type { TestingSchedule } from '../schemas/testingSchedules'
import './TestingSetup.css'
import './SetupPage.css'
import { TESTING_AMOUNTS, TESTING_FREQUENCIES, testingAmountAvailable, testingAmountRequiredPlan, scheduleTestingAmount, testingScheduleChanges, type TestingFrequency } from '../lib/continuousTestingSetup'

const FREQUENCY_HINTS: Record<TestingFrequency, string> = {
  hourly: 'Catches regressions within the hour',
  daily: 'A steady check of your main flows',
  weekly: 'A deeper pass across the whole app',
}

export default function ContinuousTestingSetup({ projectId, canEdit, onDone, showDescription = true }: { projectId: string; canEdit: boolean; onDone?: () => void; showDescription?: boolean }) {
  const schedules = useGetTestingSchedulesQuery(projectId)
  return <div className="testing-settings">
    {showDescription && <p>Automatically test your app for bugs. Choose how much to test each hour, day, or week and what to focus on.</p>}
    {schedules.isLoading && <p role="status">Loading testing settings…</p>}
    {schedules.isError && <p role="alert">Could not load testing settings. <button type="button" onClick={() => void schedules.refetch()}>Retry</button></p>}
    {schedules.currentData && <TestingEditor key={projectId} projectId={projectId} canEdit={canEdit} onDone={onDone} initialSchedules={schedules.currentData.schedules} />}
  </div>
}

function TestingEditor({ projectId, canEdit, onDone, initialSchedules }: { projectId: string; canEdit: boolean; onDone?: () => void; initialSchedules: TestingSchedule[] }) {
  const usage = useGetTestingSetupPlanQuery(projectId)
  const [save, saving] = useSaveTestingScheduleMutation()
  const [schedules, setSchedules] = useState(initialSchedules)
  const [choices, setChoices] = useState<Partial<Record<TestingFrequency, number>>>(() => Object.fromEntries(TESTING_FREQUENCIES.map(frequency => {
    const schedule = initialSchedules.find(item => item.config.cadence === frequency && item.config.enabled)
    return [frequency, schedule ? scheduleTestingAmount(schedule) : undefined]
  })))
  const [focus, setFocus] = useState<Partial<Record<TestingFrequency, string>>>({})
  const [dirty, setDirty] = useState<Partial<Record<TestingFrequency, boolean>>>({})
  const [message, setMessage] = useState('')
  function choose(frequency: TestingFrequency, journeys: number | undefined) {
    setChoices(previous => ({ ...previous, [frequency]: journeys }))
    setDirty(previous => ({ ...previous, [frequency]: true }))
    setMessage('')
  }
  function currentFocus(frequency: TestingFrequency) {
    if (focus[frequency] !== undefined) return focus[frequency] ?? ''
    const existing = schedules.find(item => item.config.cadence === frequency && item.config.enabled) ?? schedules.find(item => item.config.cadence === frequency)
    return existing?.config.target.kind === 'exploration' ? existing.config.target.prompt : ''
  }
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const busy = submitting || saving.isLoading || !canEdit || !usage.currentData || usage.isError
  const enabled = TESTING_FREQUENCIES.filter(frequency => choices[frequency] !== undefined)
  const canSave = Object.values(dirty).some(Boolean) && (enabled.length > 0 || schedules.some(schedule => schedule.config.enabled))
  return <form className="continuous-testing-setup setup-section" aria-labelledby="continuous-testing-title" onSubmit={async event => {
    event.preventDefault(); setError('')
    if (busy || !canSave) return
    setSubmitting(true)
    try {
      for (const cadence of TESTING_FREQUENCIES.filter(frequency => dirty[frequency])) {
        const journeys = choices[cadence]
        if (journeys !== undefined && !testingAmountAvailable(usage.currentData?.plan_key, cadence, journeys)) throw new Error('Choose an amount available on your plan.')
        for (const change of testingScheduleChanges(schedules, cadence, journeys, focus[cadence])) {
          const updated = await save({ projectId, ...change }).unwrap()
          setSchedules(previous => [...previous.filter(schedule => schedule.id !== updated.id), updated])
        }
        setDirty(previous => ({ ...previous, [cadence]: false }))
      }
      setMessage('Testing settings saved.')
      onDone?.()
    } catch { setError('Could not save all testing choices. Retry to finish saving; already saved schedules will be updated.') } finally { setSubmitting(false) }
  }}>
    <div className="setup-section__header">
      <div>
        <h2 id="continuous-testing-title">Recurring tests</h2>
        <p>Pick how much QA tests on each cadence. Each journey is one end-to-end check of a user flow.</p>
      </div>
    </div>
    {usage.isLoading && <p className="continuous-testing-setup__notice" role="status">Checking your plan…</p>}
    {usage.isError && <p className="continuous-testing-setup__notice" role="alert">Could not check your plan. <button type="button" onClick={() => void usage.refetch()}>Retry</button></p>}
    {TESTING_FREQUENCIES.map(frequency => <fieldset key={frequency} disabled={busy} aria-labelledby={`testing-label-${frequency}`}>
      <div className="continuous-testing-setup__row">
        <div className="continuous-testing-setup__frequency">
          <strong id={`testing-label-${frequency}`}>{frequency[0]!.toUpperCase()}{frequency.slice(1)} testing</strong>
          <span>{FREQUENCY_HINTS[frequency]}</span>
        </div>
        <div className="continuous-testing-setup__amounts">
          <button type="button" aria-pressed={choices[frequency] === undefined} onClick={() => choose(frequency, undefined)}>Off</button>
          {TESTING_AMOUNTS.map(amount => {
            const available = testingAmountAvailable(usage.currentData?.plan_key, frequency, amount.journeys)
            const reason = `${amount.name} ${frequency} testing requires the ${testingAmountRequiredPlan(frequency, amount.journeys)} plan or higher.`
            const tooltipId = `testing-${frequency}-${amount.journeys}-plan`
            return <span key={amount.name} className="continuous-testing-setup__option" tabIndex={available ? undefined : 0} aria-describedby={available ? undefined : tooltipId}>
              <button type="button" disabled={!available} aria-pressed={choices[frequency] === amount.journeys} onClick={() => choose(frequency, amount.journeys)}>
                <strong>{amount.name}</strong><small>{amount.journeys} journeys</small>
              </button>
              {!available && <span id={tooltipId} role="tooltip" className="continuous-testing-setup__tooltip">{reason}</span>}
            </span>
          })}
        </div>
      </div>
      <details className="continuous-testing-setup__focus">
        <summary>Anything to focus on?</summary>
        <label htmlFor={`testing-focus-${frequency}`}>What should {frequency} testing cover?</label>
        <textarea id={`testing-focus-${frequency}`} rows={3} maxLength={10000} value={currentFocus(frequency)} placeholder="For example, checkout, sign-in, and recent changes" onChange={event => {
          setFocus(previous => ({ ...previous, [frequency]: event.target.value }))
          setDirty(previous => ({ ...previous, [frequency]: true }))
          setMessage('')
        }} />
      </details>
    </fieldset>)}
    <div className="setup-section__footer">
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      <button className="testing-setup__primary continuous-testing-setup__confirm" type="submit" disabled={busy || !canSave}>{submitting ? 'Saving…' : 'Save test schedule'}</button>
    </div>
  </form>
}
